import { useEffect, useState, useRef } from 'react'
import { useI18n } from '@/lib/i18n'
import type { EventConfig } from '@/types'
import { CameraIcon, FlipHorizontal, RotateCw, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react'

// All resolution + fps combos the UI exposes.
// The browser will use these as ideal constraints, it falls back gracefully
// if the camera doesn't support the exact combination.
const RESOLUTION_PRESETS = [
  { label: '4K · 30fps',    width: 3840, height: 2160, fps: 30 },
  { label: '1440p · 60fps', width: 2560, height: 1440, fps: 60 },
  { label: '1440p · 30fps', width: 2560, height: 1440, fps: 30 },
  { label: '1080p · 60fps', width: 1920, height: 1080, fps: 60 },
  { label: '1080p · 30fps', width: 1920, height: 1080, fps: 30 },
  { label: '896p · 60fps',  width: 1600, height:  896, fps: 60 },
  { label: '896p · 30fps',  width: 1600, height:  896, fps: 30 },
  { label: '720p · 60fps',  width: 1280, height:  720, fps: 60 },
  { label: '720p · 30fps',  width: 1280, height:  720, fps: 30 },
  { label: '576p · 60fps',  width: 1024, height:  576, fps: 60 },
  { label: '576p · 30fps',  width: 1024, height:  576, fps: 30 },
  { label: '540p · 60fps',  width:  960, height:  540, fps: 60 },
  { label: '540p · 30fps',  width:  960, height:  540, fps: 30 },
] as const

export default function CameraSettings({ event, onChange }: { event: EventConfig; onChange: (u: Partial<EventConfig>) => void }) {
  const { t } = useI18n()
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [permission, setPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt')
  const [justGranted, setJustGranted] = useState(false)
  const [previewActive, setPreviewActive] = useState(false)
  const [streamInfo, setStreamInfo] = useState<{ width: number; height: number; fps: number } | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const previewGeneration = useRef(0)

  // Fallback for existing events that may not have frameRate stored yet
  const currentFps = event.camera.frameRate ?? 30

  const enumerate = async () => {
    try {
      const devs = await navigator.mediaDevices.enumerateDevices()
      const cams = devs.filter((d) => d.kind === 'videoinput')
      setDevices(cams)
      if (cams.some((d) => d.label)) setPermission('granted')
    } catch {
      setPermission('denied')
    }
  }

  const stopStream = () => {
    previewGeneration.current++
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    if (videoRef.current) videoRef.current.srcObject = null
    setPreviewActive(false)
    setStreamInfo(null)
  }

  const startPreview = async () => {
    stopStream()
    const generation = previewGeneration.current
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: event.camera.deviceId ? { exact: event.camera.deviceId } : undefined,
          width:     { ideal: event.camera.resolution.width },
          height:    { ideal: event.camera.resolution.height },
          frameRate: { ideal: currentFps },
        },
      })
      if (generation !== previewGeneration.current) {
        stream.getTracks().forEach(track => track.stop())
        return
      }
      streamRef.current = stream
      if (videoRef.current) videoRef.current.srcObject = stream
      setPreviewActive(true)
      const track = stream.getVideoTracks()[0]
      const settings = track.getSettings()
      setStreamInfo({
        width:  settings.width     ?? 0,
        height: settings.height    ?? 0,
        fps:    Math.round(settings.frameRate ?? 0),
      })
    } catch {
      if (generation !== previewGeneration.current) return
      setPreviewActive(false)
    }
  }

  useEffect(() => {
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        stream.getTracks().forEach((t) => t.stop())
        setPermission('granted')
        setJustGranted(true)
        enumerate()
      })
      .catch(() => {
        setPermission('denied')
        enumerate()
      })
  }, [])

  // Auto-start preview when permission is granted
  useEffect(() => {
    if (permission === 'granted') {
      startPreview()
    }
    return () => stopStream()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission])

  // Live-refresh preview whenever device, resolution, or frameRate changes
  useEffect(() => {
    if (previewActive) startPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.camera.deviceId, event.camera.resolution.width, event.camera.resolution.height, event.camera.frameRate])

  const update = (key: keyof EventConfig['camera'], value: unknown) => {
    onChange({ camera: { ...event.camera, [key]: value } })
  }

  const applyPreset = (preset: typeof RESOLUTION_PRESETS[number]) => {
    onChange({
      camera: {
        ...event.camera,
        resolution: { width: preset.width, height: preset.height },
        frameRate: preset.fps,
      },
    })
    // The useEffect above will detect the change and restart the preview automatically
  }

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h2 className="text-xl font-bold">{t('editor.cameraSettings')}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t('camera.settingsDesc')}</p>
      </div>

      {/* Permission status */}
      {permission === 'denied' && (
        <div className="flex items-start gap-3 rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">{t('camera.permissionDenied')}</div>
            <div className="mt-0.5 opacity-80">{t('camera.permissionDeniedDesc')}</div>
          </div>
        </div>
      )}
      {justGranted && permission === 'granted' && (
        <div className="flex items-center gap-3 rounded-xl border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-400">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {t('camera.permissionGranted')}
        </div>
      )}

      {/* Camera Device */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <CameraIcon className="h-4 w-4 text-primary" /> {t('camera.device')}
        </div>
        <select
          value={event.camera.deviceId}
          onChange={(e) => update('deviceId', e.target.value)}
          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
        >
          <option value="">{t('inheritance.automaticCamera')}</option>
          {event.camera.deviceId && !devices.some(d => d.deviceId === event.camera.deviceId) && <option value={event.camera.deviceId}>{t('inheritance.unavailableDevice')}</option>}
          {devices.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>{d.label || `${t('camera.photo')} ${d.deviceId.slice(0, 8)}`}</option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">{t('inheritance.deviceHint')}</p>
      </div>

      {/* Resolution + FPS */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
        <div className="space-y-0.5">
          <div className="text-sm font-semibold">{t('camera.resolution')} &amp; Frame Rate</div>
          <p className="text-xs text-muted-foreground">
            Your camera may not support every option. The browser will pick the closest available mode.
            The preview badge shows the actual negotiated resolution and fps.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {RESOLUTION_PRESETS.map((preset) => {
            const active =
              event.camera.resolution.width  === preset.width &&
              event.camera.resolution.height === preset.height &&
              currentFps === preset.fps
            return (
              <button
                key={`${preset.width}x${preset.height}@${preset.fps}`}
                onClick={() => applyPreset(preset)}
                className={`rounded-lg border px-3 py-2.5 text-left transition-all ${
                  active
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-border hover:border-border/80 hover:bg-muted text-muted-foreground'
                }`}
              >
                <div className="text-sm font-semibold leading-tight">
                  {preset.width}×{preset.height}
                </div>
                <div className={`flex items-center gap-1.5 text-xs mt-0.5 ${active ? 'text-primary/70' : 'text-muted-foreground/70'}`}>
                  {preset.fps}fps
                  {preset.fps === 60 && (
                    <span className={`rounded px-1 py-0.5 text-[10px] font-bold leading-none ${
                      active ? 'bg-primary/20 text-primary' : 'bg-amber-500/15 text-amber-400'
                    }`}>
                      60fps
                    </span>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Mirror + Rotation */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
        <div className="text-sm font-semibold">{t('camera.orientation')}</div>
        <label className="flex items-center gap-3 cursor-pointer rounded-lg border border-border bg-background px-4 py-3 hover:bg-muted transition-colors">
          <input type="checkbox" checked={event.camera.mirrorLiveView} onChange={(e) => update('mirrorLiveView', e.target.checked)} />
          <FlipHorizontal className="h-4 w-4 text-muted-foreground" />
          <div>
            <div className="text-sm font-medium">{t('camera.mirror')}</div>
            <div className="text-xs text-muted-foreground">{t('camera.mirrorDesc')}</div>
          </div>
        </label>
        <div>
          <div className="mb-2 text-xs text-muted-foreground flex items-center gap-1.5">
            <RotateCw className="h-3 w-3" /> {t('camera.rotation')}
          </div>
          <div className="flex gap-2">
            {[0, 90, 180, 270].map((deg) => (
              <button
                key={deg}
                onClick={() => update('rotation', deg as 0 | 90 | 180 | 270)}
                className={`flex-1 rounded-lg border py-2 text-sm font-medium transition-all ${
                  event.camera.rotation === deg
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-border hover:bg-muted text-muted-foreground'
                }`}
              >
                {deg}°
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">{t('camera.preview')}</div>
          <div className="flex items-center gap-2">
            {streamInfo && (
              <span className="rounded-full bg-green-500/15 border border-green-500/20 px-2 py-0.5 text-[11px] font-medium text-green-400">
                {streamInfo.width}×{streamInfo.height} @ {streamInfo.fps}fps
              </span>
            )}
            <button
              onClick={previewActive ? stopStream : () => startPreview()}
              className="flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-1.5 text-xs font-medium hover:bg-secondary/80 transition-colors"
            >
              <RefreshCw className="h-3 w-3" />
              {previewActive ? t('camera.restart') : t('camera.start')}
            </button>
          </div>
        </div>
        <div
          className="relative overflow-hidden rounded-xl border border-border bg-black"
          style={{ aspectRatio: '16/9' }}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="h-full w-full object-cover"
            style={{ transform: event.camera.mirrorLiveView ? 'scaleX(-1)' : undefined }}
          />
          {!previewActive && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
              <CameraIcon className="h-8 w-8 opacity-30" />
              <span className="text-xs">{t('camera.previewInactive')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
